/**
 * Whether an account may see a sidebar item a builder made. The item names the
 * permissions it needs the way the introspection endpoint names them; an item
 * that names none is shown to everyone, and the `*.*.*` wildcard an
 * administrator holds satisfies any of them.
 */
export function isSidebarItemVisible(
    props: Record<string, unknown> | undefined,
    permissions: readonly string[],
): boolean {
    return holdsPermissions(readSidebarItemPermissions(props), permissions);
}

function readSidebarItemPermissions(
    props: Record<string, unknown> | undefined,
): string[] {
    const entries = props?.permissions;
    if (!Array.isArray(entries)) return [];

    return entries
        .map((entry) => {
            if (typeof entry === "string") return entry;
            if (entry && typeof entry === "object") {
                const value = (entry as { permission?: unknown }).permission;
                return typeof value === "string" ? value : "";
            }
            return "";
        })
        .map((permission) => permission.trim())
        .filter(Boolean);
}

/**
 * Whether an account holds every permission named, the way the introspection
 * endpoint names them. Naming none is satisfied by anyone, and the `*.*.*`
 * wildcard an administrator holds satisfies any of them. It is the rule a
 * builder-made item and a component shown under a permission both read.
 */
export function holdsPermissions(
    required: readonly string[] | undefined,
    granted: readonly string[],
): boolean {
    return (
        !required ||
        required.length === 0 ||
        granted.includes("*.*.*") ||
        required.every((permission) => granted.includes(permission))
    );
}
